import BigNumber from "bignumber.js"
import { LineSeriesOption } from "echarts"
import { MutableRefObject, useEffect, useRef, useState } from "react"
import { Button, Card, Form } from "react-bootstrap"
import DatePicker from "react-datepicker"
import { average as findAverage } from "simple-statistics"
import { calcMarkLineOffset } from "../getSpeedChartOptionsHelpers"
import { calcOverUnderPerformance } from "../helpers/calculateSensitivity"
import { getPortStaysData } from "../helpers/getPortStaysData"
import { usePrevetTabStateContext } from "../hooks/prevetContext"
import { VesselStay } from "../hooks/useFetchVesselStays"
import { SegmentSensitivityData } from "../hooks/usePrevetGetSensitivityDataForSegment"
import {
  SelectOption,
  VesselListSingle,
  VesselSegmentInputSelection,
} from "./PrevetMain"
import { ChosenVesselForSelect, PrevetVesselSearch } from "./PrevetVesselSearch"

interface PrevetSearchWidgetProps {
  ladenFuelCon: string
  ladenSeaDays: string
  describedLadenSpeed: string
  describedBallastSpeed: string
  ballastFuelCon: string
  ballastSeaDays: string
  setLadenFuelCon: (value: string) => void
  setLadenSeaDays: (value: string) => void
  setDescribedLadenSpeed: (value: string) => void
  setDescribedBallastSpeed: (value: string) => void
  setBallastFuelCon: (value: string) => void
  setBallastSeaDays: (value: string) => void
  segmentSensitivityData: SegmentSensitivityData | undefined
  ladenChartInstance: MutableRefObject<any>
  ballastChartInstance: MutableRefObject<any>
  speedGraphLadenAvgSpeed: number | undefined
  speedGraphBallastAvgSpeed: number | undefined
  portStaysLadenAvgSpeed: number | undefined
  portStaysBallastAvgSpeed: number | undefined
  realLadenMaxSpeed: number | undefined
  realBallastMaxSpeed: number | undefined
  vesselName: string
  defaultFuelCon: number
  setUserLadenVesselSpeed: (speed: string | undefined) => void
  setUserBallastVesselSpeed: (speed: string | undefined) => void
  vesselsData: VesselListSingle[] | undefined
  getVesselsLoading: boolean
  getVesselDetailsLoading: boolean
  chosenSegment: VesselSegmentInputSelection
  getChosenVessel: (args: ChosenVesselForSelect) => void
  setChosenSegment: (segment: VesselSegmentInputSelection) => void
  getVessels: ({
    segments,
    controller,
  }: {
    segments: string[]
    controller?: AbortController
  }) => Promise<void>
  vesselSegments: VesselSegmentInputSelection[]
  clearChosenVesselData: () => void
  getSegmentSensitivityData: (segment: string) => void
  chosenVessel: SelectOption | undefined
  setChosenVessel: React.Dispatch<
    React.SetStateAction<SelectOption | undefined>
  >
  selectedDate: string // The selected date in yyyy-mm-dd format
  setSelectedDate: React.Dispatch<React.SetStateAction<string>> // Setter function for the date
  showTradingModal: () => void
}

function PrevetSearchWidget({
  ladenFuelCon,
  ladenSeaDays,
  describedLadenSpeed,
  describedBallastSpeed,
  ballastFuelCon,
  ballastSeaDays,
  setLadenFuelCon,
  setLadenSeaDays,
  setDescribedLadenSpeed,
  setDescribedBallastSpeed,
  setBallastFuelCon,
  setBallastSeaDays,
  ladenChartInstance,
  ballastChartInstance,
  speedGraphLadenAvgSpeed,
  speedGraphBallastAvgSpeed,
  portStaysLadenAvgSpeed,
  portStaysBallastAvgSpeed,
  realLadenMaxSpeed,
  realBallastMaxSpeed,
  segmentSensitivityData,
  vesselName,
  defaultFuelCon,
  setUserLadenVesselSpeed,
  setUserBallastVesselSpeed,
  vesselsData,
  getVesselsLoading,
  getVesselDetailsLoading,
  chosenSegment,
  getChosenVessel,
  setChosenSegment,
  getVessels,
  vesselSegments,
  clearChosenVesselData,
  getSegmentSensitivityData,
  chosenVessel,
  setChosenVessel,
  selectedDate,
  setSelectedDate,
  showTradingModal,
}: PrevetSearchWidgetProps) {
  const { setTabStateValue, getSelectedIndex } =
    usePrevetTabStateContext()

  const spotIndexName =
    segmentSensitivityData?.benchmark.spot_index_name || "P5TC"
  const defaultHirePrice = segmentSensitivityData?.benchmark.spot_price || ""
  const defaultFuelPrice = segmentSensitivityData?.benchmark.fuel_price || ""

  const [describedBallastSpeedTouched, setDescribedBallastSpeedTouched] =
    useState(false)

  const [describedLadenSpeedTouched, setDescribedLadenSpeedTouched] =
    useState(false)

  // Keep track of minor customization to grey out the Std Vsl box
  const [isStandardVesselBoxGray, setIsStandardVesselBoxGray] = useState(false)

  const [hirePrice, setHirePrice] = useState(String(defaultHirePrice))
  const [fuelPrice, setFuelPrice] = useState(String(defaultFuelPrice))

  const [performanceExposure, setPerformanceExposure] = useState<
    number | undefined
  >()

  const [vesselComparisonMessage, setVesselComparisonMessage] = useState(
    `Std vsl / ${vesselName}`
  )
  const [currentSelectedIndex, setCurrentSelectedIndex] = useState(
    getSelectedIndex()
  )
  // Ref to store previous values
  const previousStateValues = useRef({
    selectedIndex: currentSelectedIndex,
    chosenVessel,
    selectedDate,
    describedBallastSpeed,
    ballastFuelCon,
    ballastSeaDays,
    describedLadenSpeed,
    ladenFuelCon,
    ladenSeaDays,
  })

  useEffect(() => {
    const currentSelectedIndex = getSelectedIndex()
    setCurrentSelectedIndex(currentSelectedIndex)
    previousStateValues.current = {
      ...previousStateValues.current,
      selectedIndex: currentSelectedIndex,
    }
  }, [getSelectedIndex()])

  useEffect(() => {
    setTabStateValue("date", selectedDate, undefined)
    setTabStateValue("cvdBallastSpeed", describedBallastSpeed, undefined)
    setTabStateValue("cvdBallastFuelCon", ballastFuelCon, undefined)
    setTabStateValue("cvdBallastSeaDays", ballastSeaDays, undefined)
    setTabStateValue("cvdLadenSpeed", describedLadenSpeed, currentSelectedIndex)
    setTabStateValue("cvdLadenFuelCon", ladenFuelCon, undefined)
    setTabStateValue("cvdLadenSeaDays", ladenSeaDays, undefined)
  }, [
    currentSelectedIndex,
    chosenVessel,
    selectedDate,
    describedBallastSpeed,
    ballastFuelCon,
    ballastSeaDays,
    describedLadenSpeed,
    ladenFuelCon,
    ladenSeaDays,
  ])

  useEffect(() => {
    setFuelPrice(String(segmentSensitivityData?.benchmark.fuel_price))
  }, [segmentSensitivityData?.benchmark.fuel_price])

  useEffect(() => {
    const ladenAvgSpeedForCalculation = speedGraphLadenAvgSpeed
      ? speedGraphLadenAvgSpeed
      : portStaysLadenAvgSpeed
    const ballastAvgSpeedForCalculation = speedGraphBallastAvgSpeed
      ? speedGraphBallastAvgSpeed
      : portStaysBallastAvgSpeed

    if (!ladenAvgSpeedForCalculation && !ballastAvgSpeedForCalculation) {
      return
    }

    const performance = calcPerformance({
      describedLadenSpeed,
      describedBallastSpeed,
      speedGraphLadenAvgSpeed: ladenAvgSpeedForCalculation,
      speedGraphBallastAvgSpeed: ballastAvgSpeedForCalculation,
      ladenFuelCon,
      ballastFuelCon,
      hirePrice,
      fuelPrice,
      ladenSeaDays,
      ballastSeaDays,
    })
    setPerformanceExposure(performance)
  }, [
    describedLadenSpeed,
    describedBallastSpeed,
    portStaysLadenAvgSpeed,
    portStaysBallastAvgSpeed,
    speedGraphLadenAvgSpeed,
    speedGraphBallastAvgSpeed,
    ladenFuelCon,
    ballastFuelCon,
    hirePrice,
    fuelPrice,
    ladenSeaDays,
    ballastSeaDays,
  ])

  function updateChart({
    isLaden,
    speed,
    isReset,
  }: {
    isLaden: boolean
    speed: string
    isReset: boolean
  }) {
    if (
      (isLaden && !speedGraphLadenAvgSpeed) ||
      (!isLaden && !speedGraphBallastAvgSpeed)
    ) {
      return
    }
    const avgSpeed = isLaden
      ? speedGraphLadenAvgSpeed
      : speedGraphBallastAvgSpeed

    const chart = isLaden ? ladenChartInstance : ballastChartInstance
    const opts = chart.current.getOption()

    const maxYAxis = isLaden ? realLadenMaxSpeed : realBallastMaxSpeed

    const yAxis = !maxYAxis // Grow or shrink yAxis depending on user input
      ? [{ ...opts.yAxis[0] }]
      : [
          {
            ...opts.yAxis[0],
            max: [
              Number(speed) > maxYAxis // If user input TC desc > speed graph max
                ? Number(speed) + 1
                : BigNumber(maxYAxis + 1)
                    .decimalPlaces(0, BigNumber.ROUND_HALF_CEIL)
                    .toString(),
            ],
          },
        ]

    // When focusing any input, draw the usr vessel line on speed graph
    chart.current.setOption({
      ...opts,
      series: [
        {
          ...(opts.series as LineSeriesOption[])[0],
        },
        {
          ...(opts.series as LineSeriesOption[])[1],
        },
        {
          ...(opts.series as LineSeriesOption[])[2],
        },
        !isReset
          ? {
              name: `User Vsl`,
              markLine: {
                symbolSize: 7,
                lineStyle: {
                  width: 1,
                  opacity: 0.75,
                },
                data: [
                  {
                    name: "user vessel",
                    yAxis: Number(speed),
                    label: {
                      offset: calcMarkLineOffset({
                        label: Number(speed),
                        compared: avgSpeed as number,
                      }),
                      fontSize: 10,
                      formatter: (params: any) => {
                        return `${BigNumber(params.data.yAxis).toFormat(
                          1
                        )} User Vsl`
                      },
                    },
                  },
                ],
              },
            }
          : {
              name: "User Vsl", // Put this on its own markLine because of symbol: none
              type: "line",
              markLine: {
                lineStyle: {
                  width: 0,
                  opacity: 0,
                },
              },
            },
      ],
      yAxis,
    })
  }

  const updateVesselComparisonMessage = () => {
    setVesselComparisonMessage(`User vsl / ${vesselName}`)
  }

  // Handle date change
  const handleDateChange = (date: Date | null) => {
    if (date) {
      const formattedDate = date.toISOString().split("T")[0] // Format: yyyy-mm-dd
      setSelectedDate(formattedDate) // Update the state in the parent
      // setTabStateValue("date", formattedDate, undefined)
    }
  }

  const handleTodayButton = () => {
    setSelectedDate(new Date().toISOString().split("T")[0])
  }

  return (
    <Card className="PrevetContainer_SearchWidget">
      <Card.Body>
        <PrevetVesselSearch
          vesselsData={vesselsData}
          getVesselsLoading={getVesselsLoading}
          getVesselDetailsLoading={getVesselDetailsLoading}
          chosenSegment={chosenSegment}
          getChosenVessel={getChosenVessel}
          setChosenSegment={setChosenSegment}
          getVessels={getVessels}
          vesselSegments={vesselSegments}
          clearChosenVesselData={clearChosenVesselData}
          chosenVessel={chosenVessel}
          setChosenVessel={setChosenVessel}
        />
        {/* Ballast and laden speed */}
        <div className="text-muted mt-2" style={{ fontSize: "0.75rem" }}>
          Circulated Vessel Description (insert)
        </div>
        <div className="d-flex flex-nowrap border border-gray rounded px-2 pt-3 pb-2 align-items-center justify-content-between">
          <div
            className="PrevetCards_CircVesselText text-center"
            style={{ color: "#ff5b29" }}
          >
            Ballast
          </div>
          {/* kts - Ballast */}
          <div className="d-flex g-0 gap-0 align-items-baseline position-relative">
            <Form.Control
              className="PrevetCards_CircVesselForm my-auto"
              placeholder="Ballast Speed"
              aria-label="Described speed in Knots"
              disabled={
                Boolean(speedGraphBallastAvgSpeed) === false &&
                Boolean(portStaysBallastAvgSpeed) === false
              }
              value={
                describedBallastSpeedTouched
                  ? describedBallastSpeed
                  : BigNumber(describedBallastSpeed).toFormat(1)
              }
              onFocus={(e) => {
                updateChart({
                  isLaden: false,
                  speed: e.target.value,
                  isReset: false,
                })
                updateChart({
                  isLaden: true,
                  speed: describedLadenSpeed,
                  isReset: false,
                })
                setUserBallastVesselSpeed(e.target.value)
                setUserLadenVesselSpeed(describedLadenSpeed) // Set the User Vsl line in Speed Graph Modal / All Ocean Zones Modal
                updateVesselComparisonMessage()
                setIsStandardVesselBoxGray(true)
              }}
              onChange={(e) => {
                setDescribedBallastSpeedTouched(true)
                setDescribedBallastSpeed(e.target.value)
                setUserBallastVesselSpeed(e.target.value)
                updateChart({
                  isLaden: false,
                  speed: e.target.value,
                  isReset: false,
                })
              }}
            />
            <div className="PrevetCards_CircVesselText_SubText">kt</div>
            <div className="fs-4 px-1">/</div>
          </div>
          {/* MT - Ballast */}
          <div className="d-flex g-0 gap-0 align-items-baseline position-relative">
            <Form.Control
              className="PrevetCards_CircVesselForm my-auto"
              aria-label="Metric Tons"
              value={ballastFuelCon}
              disabled={
                Boolean(speedGraphBallastAvgSpeed) === false &&
                Boolean(portStaysBallastAvgSpeed) === false
              }
              onFocus={(e) => {
                updateChart({
                  isLaden: true,
                  speed: describedLadenSpeed,
                  isReset: false,
                })
                updateChart({
                  isLaden: false,
                  speed: describedBallastSpeed,
                  isReset: false,
                })
                setUserLadenVesselSpeed(describedLadenSpeed)
                setUserBallastVesselSpeed(describedBallastSpeed) // Set the User Vsl line in Speed Graph Modal / All Ocean Zones Modal
                updateVesselComparisonMessage()
                setIsStandardVesselBoxGray(true)
              }}
              onChange={(e) => {
                setBallastFuelCon(e.target.value)
                // setTabStateValue("cvdBallastFuelCon", e.target.value, undefined)
              }}
            />
            <div className="PrevetCards_CircVesselText_SubText">mt</div>
            <div className="fs-4 px-1">/</div>
          </div>
          {/* Sea days - ballast */}
          <div className="d-flex g-0 gap-0 align-items-baseline position-relative">
            <Form.Control
              className="PrevetCards_CircVesselForm my-auto"
              aria-label="Days"
              value={ballastSeaDays}
              disabled={
                Boolean(speedGraphBallastAvgSpeed) === false &&
                Boolean(portStaysBallastAvgSpeed) === false
              }
              onFocus={(e) => {
                updateChart({
                  isLaden: true,
                  speed: describedLadenSpeed,
                  isReset: false,
                })
                updateChart({
                  isLaden: false,
                  speed: describedBallastSpeed,
                  isReset: false,
                })
                setUserLadenVesselSpeed(describedLadenSpeed)
                setUserBallastVesselSpeed(describedBallastSpeed) // Set the User Vsl line in Speed Graph Modal / All Ocean Zones Modal
                updateVesselComparisonMessage()
                setIsStandardVesselBoxGray(true)
              }}
              onChange={(e) => {
                setBallastSeaDays(e.target.value)
                // setTabStateValue("cvdBallastSeaDays", e.target.value, undefined)
              }}
            />
            <div className="PrevetCards_CircVesselText_SubText_SeaDays">
              sea days
            </div>
          </div>
          {/* divider  */}
          <div className="text-muted fs-4 mx-2">|</div>
          {/* Laden */}
          <div
            className="PrevetCards_CircVesselText"
            style={{ color: "#001546" }}
          >
            Laden
          </div>
          <div className="d-flex g-0 gap-0 align-items-baseline position-relative">
            {/* kts - Laden */}
            <Form.Control
              className="PrevetCards_CircVesselForm my-auto"
              aria-label="Knots"
              disabled={
                Boolean(speedGraphLadenAvgSpeed) === false &&
                Boolean(portStaysLadenAvgSpeed) === false
              }
              placeholder="Laden Speed"
              value={
                describedLadenSpeedTouched
                  ? describedLadenSpeed
                  : BigNumber(describedLadenSpeed).toFormat(1)
              }
              onFocus={(e) => {
                updateChart({
                  isLaden: true,
                  speed: e.target.value,
                  isReset: false,
                })
                updateChart({
                  isLaden: false,
                  speed: describedBallastSpeed,
                  isReset: false,
                })
                setUserLadenVesselSpeed(e.target.value)
                setUserBallastVesselSpeed(describedBallastSpeed) // Set the User Vsl line in Speed Graph Modal / All Ocean Zones Modal
                updateVesselComparisonMessage()
                setIsStandardVesselBoxGray(true)
              }}
              onChange={(e) => {
                setDescribedLadenSpeedTouched(true)
                setDescribedLadenSpeed(e.target.value)
                setUserLadenVesselSpeed(e.target.value)
                updateChart({
                  isLaden: true,
                  speed: e.target.value,
                  isReset: false,
                })
              }}
            />
            <div className="PrevetCards_CircVesselText_SubText">kt</div>
            <div className="fs-4 px-1">/</div>
          </div>
          <div className="d-flex g-0 gap-0 align-items-baseline position-relative">
            {/* MT - laden */}
            <Form.Control
              className="PrevetCards_CircVesselForm my-auto"
              aria-label="Metric Tons"
              value={ladenFuelCon}
              disabled={
                Boolean(speedGraphLadenAvgSpeed) === false &&
                Boolean(portStaysLadenAvgSpeed) === false
              }
              onFocus={(e) => {
                updateChart({
                  isLaden: true,
                  speed: describedLadenSpeed,
                  isReset: false,
                })
                updateChart({
                  isLaden: false,
                  speed: describedBallastSpeed,
                  isReset: false,
                })
                setUserLadenVesselSpeed(describedLadenSpeed)
                setUserBallastVesselSpeed(describedBallastSpeed) // Set the User Vsl line in Speed Graph Modal / All Ocean Zones Modal
                updateVesselComparisonMessage()
                setIsStandardVesselBoxGray(true)
              }}
              onChange={(e) => {
                setLadenFuelCon(e.target.value)
                // setTabStateValue("cvdLadenFuelCon", e.target.value, undefined)
              }}
            />
            <div className="PrevetCards_CircVesselText_SubText">mt</div>
            <div className="fs-4 px-1">/</div>
          </div>
          <div className="d-flex g-0 gap-0 align-items-baseline position-relative">
            {/* Sea days - Laden */}
            <Form.Control
              className="PrevetCards_CircVesselForm my-auto"
              aria-label="Days"
              disabled={
                Boolean(speedGraphLadenAvgSpeed) === false &&
                Boolean(portStaysLadenAvgSpeed) === false
              }
              value={ladenSeaDays}
              onFocus={(e) => {
                updateChart({
                  isLaden: true,
                  speed: describedLadenSpeed,
                  isReset: false,
                })
                updateChart({
                  isLaden: false,
                  speed: describedBallastSpeed,
                  isReset: false,
                })
                setUserLadenVesselSpeed(describedLadenSpeed)
                setUserBallastVesselSpeed(describedBallastSpeed) // Set the User Vsl line in Speed Graph Modal / All Ocean Zones Modal
                updateVesselComparisonMessage()
                setIsStandardVesselBoxGray(true)
              }}
              onChange={(e) => {
                setLadenSeaDays(e.target.value)
                // setTabStateValue("cvdLadenSeaDays", e.target.value, undefined)
              }}
            />
            <div className="PrevetCards_CircVesselText_SubText_SeaDays">
              sea days
            </div>
          </div>
        </div>
        {/* Date section */}
        <div className="d-flex flex-nowrap justify-content-between align-items-end mt-2"
          style={{ width: "calc(100% - 10px)" }}
        >
          {/* Select Date Section */}
          <div>
            <div className="text-muted" style={{ fontSize: "0.75rem" }}>
              Select Date
            </div>
            <div className="d-flex position-relative">
              <button
                className={`btn ${
                  selectedDate === new Date().toISOString().split("T")[0]
                    ? "btn-muted border border-gray"
                    : "btn-primary"
                } position-absolute h-100`}
                onClick={handleTodayButton}
                style={{
                  zIndex: "100",
                  width: "50px",
                  fontSize: "0.7rem",
                  padding: "0",
                }}
                disabled={
                  selectedDate === new Date().toISOString().split("T")[0]
                    ? true
                    : false
                }
              >
                Today
              </button>
              <DatePicker
                selected={new Date(selectedDate)}
                onChange={handleDateChange}
                dateFormat="yyyy-MM-dd"
                maxDate={new Date()}
                minDate={new Date("2021-01-01")}
                className="form-control ps-6 w-100"
                wrapperClassName="w-100"
                showIcon={true}
              />
            </div>
          </div>
          {/* Trade Map Button */}
          <Button
            variant="primary"
            className="PrevetTrio_SpeedGraph_OceanZoneButton"
            onClick={showTradingModal}
          >
            Trade Map
          </Button>
        </div>
      </Card.Body>
    </Card>
  )
}

function roundToNearestThousand(cost: number) {
  return Math.round(cost / 1000) * 1000
}


function calcPerformance({
  describedLadenSpeed,
  describedBallastSpeed,
  speedGraphLadenAvgSpeed,
  speedGraphBallastAvgSpeed,
  ladenFuelCon,
  ballastFuelCon,
  hirePrice,
  fuelPrice,
  ladenSeaDays,
  ballastSeaDays,
}: {
  describedLadenSpeed: string
  describedBallastSpeed: string
  speedGraphLadenAvgSpeed?: number
  speedGraphBallastAvgSpeed?: number
  ladenFuelCon: string
  ballastFuelCon: string
  hirePrice: string
  fuelPrice: string
  ladenSeaDays: string
  ballastSeaDays: string
}) {
  let ladenCost
  let ballastCost

  if (speedGraphLadenAvgSpeed) {
    ladenCost =
      calcOverUnderPerformance({
        brokerSpeed: Number(describedLadenSpeed),
        actualSpeed: speedGraphLadenAvgSpeed,
        dailyFuelConsumption: Number(ladenFuelCon),
        dailyHireRate: Number(hirePrice),
        fuelPrice: Number(fuelPrice),
      }).costDeltaUSD * Number(ladenSeaDays)
  }

  if (speedGraphBallastAvgSpeed) {
    ballastCost =
      calcOverUnderPerformance({
        brokerSpeed: Number(describedBallastSpeed),
        actualSpeed: speedGraphBallastAvgSpeed,
        dailyFuelConsumption: Number(ballastFuelCon),
        dailyHireRate: Number(hirePrice),
        fuelPrice: Number(fuelPrice),
      }).costDeltaUSD * Number(ballastSeaDays)
  }

  if (ladenCost && ballastCost) {
    return roundToNearestThousand(ladenCost + ballastCost)
  }

  if (ladenCost) {
    return roundToNearestThousand(ladenCost)
  }
  if (ballastCost) {
    return roundToNearestThousand(ballastCost)
  }
}

export function getAvgSpeedFromPortStays({
  vesselStaysData,
  isLaden,
}: {
  vesselStaysData: VesselStay[] | undefined
  isLaden: boolean
}) {
  if (!vesselStaysData || vesselStaysData.length < 1) {
    return undefined
  }
  const portStaysData = getPortStaysData(vesselStaysData)
  const stays = isLaden ? portStaysData.laden : portStaysData.ballast

  if (!stays || stays.length < 1) {
    return undefined
  }

  return findAverage(stays.map((stay) => stay.avg_speed_mov))
}

export default PrevetSearchWidget
